import { IPaginatedPage } from '../types/pagination.types';

// Offset-merge for the accumulating "load more" policy, resilient to server-side list
// shifts. See "Paginated tables" → "Style 2" in CLAUDE.md for the merge semantics.
export const mergeOffsetPage = <T extends { _id?: string }>(
  existing: IPaginatedPage<T> | undefined,
  incoming: IPaginatedPage<T>,
  offset = 0,
  limit?: number,
): IPaginatedPage<T> => {
  const incomingItems = (incoming && incoming.items) || [];
  const merged = existing && existing.items ? existing.items.slice(0) : [];

  for (let i = 0; i < incomingItems.length; i += 1) {
    merged[offset + i] = incomingItems[i];
  }

  // A page shorter than the requested limit is the last page: drop any stale tail past it.
  if (limit != null && incomingItems.length < limit) {
    merged.length = offset + incomingItems.length;
  }

  // Incoming page owns its ids; drop holes and stale copies of those ids left at other
  // offsets (a shift moved them), compacting so nothing renders twice.
  const incomingStart = offset;
  const incomingEnd = offset + incomingItems.length;
  const incomingIds = new Set(incomingItems.map((item) => item && item._id).filter((id) => id != null));
  const items: T[] = [];
  for (let i = 0; i < merged.length; i += 1) {
    const item = merged[i];
    if (!item) continue;
    const withinIncoming = i >= incomingStart && i < incomingEnd;
    if (!withinIncoming && item._id != null && incomingIds.has(item._id)) continue;
    items.push(item);
  }

  return { ...incoming, items };
};
